import { useCallback, useEffect, useState } from "react";
import moment from "moment-timezone";
import ApolloClient from "apollo-client";
import { BookingRewireDataType } from "../../../../Models/BookingRewireDataType";
import { GET_BOOKING_ENTRIES_BY_SITE_AND_INVENTORY_ID } from "../../../../gql/bookingapi";
import { EventData } from "../types";
import { BOOKING_ENTRIES_QUERY_LIMIT } from "../constants";
import { getExtendedDateRange } from "../utils/dateRangeUtils";
import { transformOtherBookingEntries } from "../utils/eventsUtils";

const useOtherBookings = (
  booking: BookingRewireDataType | null,
  site: string,
  selectedDate: Date | null,
  view: string,
  timezone: string,
  client: ApolloClient<object>,
) => {
  const [otherBookings, setOtherBookings] = useState<EventData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBookings = useCallback(async () => {
    if (!booking?.bookingEntries?.length || !site || !selectedDate || !client) {
      setOtherBookings([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { min, max } = getExtendedDateRange(selectedDate, view, timezone);
      const startDate = moment(min).utc().format();
      const endDate = moment(max).utc().format();

      const inventoryIds = [...new Set(booking.bookingEntries.map((entry) => entry.inventoryId ?? entry.resource))];
      const excludeBookingId = booking.bookingEntries[0]?.bookingId || booking.id;

      const fetchPromises = inventoryIds.map(async (inventoryId) => {
        const result = await client.query({
          query: GET_BOOKING_ENTRIES_BY_SITE_AND_INVENTORY_ID,
          variables: {
            site,
            inventoryId: { eq: inventoryId },
            filter: {
              and: [{ active: { eq: true } }, { start: { le: endDate } }, { end: { ge: startDate } }],
            },
            limit: BOOKING_ENTRIES_QUERY_LIMIT,
          },
          fetchPolicy: "network-only",
        });

        const entries = result.data?.bookingEntriesBySiteAndInventoryId?.items || [];
        return transformOtherBookingEntries(entries, excludeBookingId, inventoryId);
      });

      const results = await Promise.all(fetchPromises);
      const allEntries = results.flat();
      const uniqueEntries = Array.from(new Map(allEntries.map((entry) => [entry.id, entry])).values());

      setOtherBookings(uniqueEntries);
    } catch (err) {
      setError("Failed to fetch other bookings");
      setOtherBookings([]);
    } finally {
      setIsLoading(false);
    }
  }, [booking, site, selectedDate, view, timezone, client]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);
  return { otherBookings, isLoading, error, refetch: fetchBookings };
};

export default useOtherBookings;
